/**
 * 自定义事务工具集
 */
import * as Sentry from '@sentry/vue';
import { getActiveTransaction } from '@sentry/tracing';
import {
  TransactionContext,
  Transaction,
  Span,
  SpanContext,
  CustomSamplingContext
} from '@sentry/types';

/**
 * 开启一个自定义事务
 * 如果当前已经存在事务了，那就返回当前事务
 * 可以强制停止当前事务并开启新的事务
 * @param context 事务上下文，具体参数可以参考sentry文档
 * @param customSamplingContext 自定义采样上下文
 * @param force 如果当前存在active的事务，是否强制结束该事务
 * @param autoTraceIssue 是否自动记录Issue的状态
 */
export function startTransaction(context: TransactionContext, customSamplingContext?: CustomSamplingContext, force?: boolean, autoTraceIssue?: boolean): Transaction | undefined {
  const transaction = getActiveTransaction();

  /**
   * 存在当前正在运行的事务
   */
  if (transaction) {
    if (!force) {
      return transaction;
    }
    transaction.finish();
  }

  // 如果没有source，设置自定义元信息
  context = {
    ...context,
    metadata: Object.assign(context.metadata || {}, {
      source: 'customer'
    })
  };
  const newTransaction = Sentry.startTransaction(context, customSamplingContext);
  /**
   * 绑定到当前的上下文中
   */
  Sentry.getCurrentHub().configureScope(scope => scope.setSpan(newTransaction));
  if (autoTraceIssue) {
    addIssueSpan();
  }
  return newTransaction;
}

/**
 * 结束当前事务
 */
export function endTransaction(transaction?: Transaction): void {
  transaction?.finish();
}

/**
 * 结束当前活跃事务
 */
export function endActiveTransaction():void {
  const transaction = getActiveTransaction();
  if (transaction) {
    transaction.finish();
  }
}

/**
 * 给当前事务增加中间的中间操作节点
 * @param spanContext span的上下文内容，具体参数可以参考sentry文档
 */
export function addSpan(spanContext?: Pick<SpanContext, Exclude<keyof SpanContext, 'sampled' | 'traceId' | 'parentSpanId'>>): Span | undefined {
  const transaction = getActiveTransaction();
  if (!transaction) {
    return;
  }
  return transaction.startChild(spanContext);
}

/**
 * 结束span节点
 */
export function endSpan(span: Span): void {
  span.finish();
}

/**
 * 添加通用的issue状态Span，如果有增加issue span的其他属性的，可以再做配置
 */
export function addIssueSpan(): Span | undefined {
  const spanContext = {
    data: {
      type: 'issue'
    },
    op: 'issue-status',
    description: 'show the issue status',
    tags: {
      'issue:open-status': document?.querySelector('.issue-detail') ? 'open' : 'closed'
    }
  };
  return addSpan(spanContext);
}
